import { FunctionComponent, useMemo } from 'react';
import { PriceSummary } from '../../BookingCheckoutSummary/PriceSummary';
import styles from './styles.module.scss';
import { CostSummary } from '../../../data/useCostSummary';
import classNames from 'classnames';
import { useRequestCheckoutContext } from '../../../CheckoutContext/RequestContext';
import { formatPrice } from '../formatPrice';

export type TotalSummaryProps = {
    costSummary?: CostSummary;
};

const FreeTotal: FunctionComponent = () => {
    return (
        <div className={classNames(styles.totalSummary, styles.free)}>
            <span>Total</span>
            <span>Free</span>
        </div>
    );
};

const PriceOnEnquriryTotal: FunctionComponent = () => {
    return <span className={styles.totalSummary}>Price on enquiry</span>;
};

const PriceRangeTotal: FunctionComponent<{ minimumPrice: number; maximumPrice: number }> = ({
    minimumPrice,
    maximumPrice,
}) => {
    const formattedMinimum = useMemo(() => {
        return formatPrice(minimumPrice);
    }, [minimumPrice]);

    const formattedMaximum = useMemo(() => {
        return formatPrice(maximumPrice);
    }, [maximumPrice]);

    if (formattedMinimum === formattedMaximum) {
        return <span className={styles.totalSummary}>{formattedMinimum}</span>;
    }
    return (
        <span className={styles.totalSummary}>
            {formattedMinimum} - {formattedMaximum}
        </span>
    );
};

const OffersTotal: FunctionComponent<{ costSummary?: CostSummary; currency: string }> = ({
    costSummary,
    currency,
}) => {
    const totalCost = useMemo((): number => {
        return costSummary?.total_cost || 0;
    }, [costSummary]);

    return (
        <PriceSummary
            className={styles.totalSummary}
            label="Total"
            price={totalCost}
            currency={currency}
        />
    );
};

export const TotalSummary: FunctionComponent<TotalSummaryProps> = ({ costSummary }) => {
    const { course, isInstantBooking } = useRequestCheckoutContext();
    if (isInstantBooking && (costSummary?.total_cost || 0) > 0) {
        return <OffersTotal costSummary={costSummary} currency={course.currency} />;
    }
    if (course.pricing_option.type === 'free') {
        return <FreeTotal />;
    }
    if (course.pricing_option.type === 'price-on-enquiry') {
        return <PriceOnEnquriryTotal />;
    }
    if (course.pricing_option.type === 'price-range') {
        return (
            <PriceRangeTotal
                minimumPrice={course.pricing_option.minimumPrice}
                maximumPrice={course.pricing_option.maximumPrice}
            />
        );
    }
    return <OffersTotal costSummary={costSummary} currency={course.currency} />;
};
